
import React, { useState, useEffect } from 'react';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import { QuestionProps, ModuleProps } from 'global/types';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";

import './index.css';

type DragAndDropBucketProps = {
    question: QuestionProps;
    modules: ModuleProps[];
    currentModule: ModuleProps;
    autoSave: (modules: ModuleProps[]) => void;
    setModules: (modules: ModuleProps[]) => void;
};

function DragAndDropBucket({ question, modules, currentModule, autoSave, setModules } : DragAndDropBucketProps) {
    const [selectedItemId, setSelectedItemId] = useState(null);

    const currentModuleIndex = modules.findIndex(module => module.id === currentModule.id);
    const currentAnswer = modules[currentModuleIndex].answers.find(answer => answer.questionNum === question.questionNum)?.answer;

    const handleBucketAdd = (bucketId: number, itemId?: number) => {
        const bucket = question.buckets.find(bucket => Number(bucket.bucketId) === bucketId);

        if (bucket && (itemId || selectedItemId)) {
            const bucketMaxItems = bucket.maxItems
                ? Math.min(bucket.maxItems, question.dragAndDropItems.length - 1)
                : question.dragAndDropItems.length - 1;
            
            const updatedModules = modules.map(module => {
                if (module.id !== currentModule.id) return module;

                const updatedAnswers = module.answers.map(item => {
                    if (item.questionNum === question.questionNum) {
                        let answer = item.answer;
                        if (Array.isArray(answer)) {
                            answer = answer.filter(entry => entry[1] !== (itemId ? itemId : selectedItemId));
                            answer.push([bucketId, (itemId ? itemId : selectedItemId)]);
                            
                            if (answer.filter(entry => entry[0] === bucketId).length > bucketMaxItems) return item;
                        }

                        return {
                            ...item,
                            answer: answer
                        };
                    }

                    return item;
                });

                return {
                    ...module,
                    answers: updatedAnswers
                };
            });

            autoSave(updatedModules);
            setModules(updatedModules);
            setSelectedItemId(null);
        }
    }

    const handleBucketRemove = (itemId?: number) => {
        if (itemId !== null || selectedItemId !== null) {
            const updatedModules = modules.map(module => {
                if (module.id !== currentModule.id) return module;

                const updatedAnswers = module.answers.map(item => {
                    if (item.questionNum === question.questionNum) {
                        let answer = item.answer;
                        if (Array.isArray(answer)) {
                            answer = answer.filter(entry => entry[1] !== (itemId ? itemId : selectedItemId));
                        }

                        return {
                            ...item,
                            answer: answer
                        };
                    }

                    return item;
                });

                return {
                    ...module,
                    answers: updatedAnswers
                };
            });

            autoSave(updatedModules);
            setModules(updatedModules);
            setSelectedItemId(null);
        }
    }

    const dragMove = (element: HTMLElement) => {
        let moveX = 0, moveY = 0, x = 0, y = 0, top = 0, left = 0;

        const background = element.parentElement;
    
        const dragMouseDown = (event) => {
            event.preventDefault();

            const itemId = Number(element.dataset.name);
            const rect = element.getBoundingClientRect();
            top = rect.top;
            left = rect.left;
            background.style.visibility = 'visible';
            setSelectedItemId(Number(itemId));

            if (event.type === 'mousedown') {
                x = event.clientX;
                y = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = () => closeDragElement(itemId);
            } else if (event.type === 'touchstart') {
                x = event.touches[0].clientX;
                y = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = () => closeDragElement(itemId);
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();

            if (event.type === 'mousemove') {
                moveX = x - event.clientX;
                moveY = y - event.clientY;
                x = event.clientX;
                y = event.clientY;

            } else if (event.type === 'touchmove') {
                moveX = x - event.touches[0].clientX;
                moveY = y - event.touches[0].clientY;
                x = event.touches[0].clientX;
                y = event.touches[0].clientY;
            }

            if (x > 0 && y > 0 && x < window.innerWidth && y < window.innerHeight) {
                top = top - moveY;
                left = left - moveX;

                element.style.position = 'fixed';
                element.style.top = top + 'px';
                element.style.left = left + 'px';
                element.style.width = element.parentElement.clientWidth + 'px';
                element.style.height = element.parentElement.clientHeight + 'px';
                element.style.zIndex = '2';
            }

            const items = document.getElementById('digitalSHSATModule__bucketItems');
            if (items.getBoundingClientRect().top < y && items.getBoundingClientRect().bottom > y
                && items.getBoundingClientRect().left < x && items.getBoundingClientRect().right > x
            ) {
                items.classList.add('digitalSHSATModule__bucketItemsDestinationHover');
            }
            else {
                items.classList.remove('digitalSHSATModule__bucketItemsDestinationHover');
            }

            const buckets = document.getElementsByClassName('digitalSHSATModule__bucket');
            for (let i = 0; i < buckets.length; i++) {
                if (buckets[i].getBoundingClientRect().top < y && buckets[i].getBoundingClientRect().bottom > y
                    && buckets[i].getBoundingClientRect().left < x && buckets[i].getBoundingClientRect().right > x
                ) {
                    buckets[i].classList.add('digitalSHSATModule__bucketDestinationHover');
                }
                else {
                    buckets[i].classList.remove('digitalSHSATModule__bucketDestinationHover');
                }
            }
        };
    
        const closeDragElement = (itemId?: number) => {
            const items = document.getElementById('digitalSHSATModule__bucketItems');
            items.classList.remove('digitalSHSATModule__bucketItemsDestinationHover');
            if (items.getBoundingClientRect().top < y && items.getBoundingClientRect().bottom > y
                && items.getBoundingClientRect().left < x && items.getBoundingClientRect().right > x
            ) {
                handleBucketRemove(Number(itemId));
            }

            const buckets = document.querySelectorAll<HTMLElement>('.digitalSHSATModule__bucket');
            for (let i = 0; i < buckets.length; i++) {
                buckets[i].classList.remove('digitalSHSATModule__bucketDestinationHover');
                if (buckets[i].getBoundingClientRect().top < y && buckets[i].getBoundingClientRect().bottom > y
                    && buckets[i].getBoundingClientRect().left < x && buckets[i].getBoundingClientRect().right > x
                ) {
                    handleBucketAdd(Number(buckets[i].dataset.name), Number(itemId));
                }
            }

            element.style.position = '';
            element.style.top = '';
            element.style.left = '';
            element.style.width = '';
            element.style.height = '';
            element.style.zIndex = '';
            background.style.visibility = '';
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
    
        element.onmousedown = (event) => {
            dragMouseDown(event);
        };

        element.ontouchstart = (event) => {
            dragMouseDown(event);
        };
    };

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        applyKatexWrap(document, {
            enabled: currentModule?.subject === 'English Language Arts',
        });

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    useEffect(() => {
        renderKatex();

        const bucketItems = document.getElementsByClassName('digitalSHSATModule__bucketItem');

        for (let i = 0; i < bucketItems.length; i++) {
            dragMove(bucketItems[i] as HTMLElement);
        }
    }, [question, modules]);

    return (
        <div ref={useOutsideClickHandler(() => setSelectedItemId(null))}>
            <div
                id='digitalSHSATModule__bucketItems'
                className={selectedItemId !== null && Array.isArray(currentAnswer) && currentAnswer.find(answer => answer[1] === selectedItemId)
                    ? 'digitalSHSATModule__bucketItems digitalSHSATModule__bucketItemsDestination'
                    : 'digitalSHSATModule__bucketItems'
                }
                onClick={selectedItemId !== null ? () => handleBucketRemove() : null}
            >
                {question?.dragAndDropItems.filter(item => (
                    Array.isArray(currentAnswer) && !currentAnswer.find(answer => answer[1] === item.id)
                )).map(item => (
                    <div
                        key={item.id}
                        className={selectedItemId === item.id
                            ? 'digitalSHSATModule__bucketItemBackground digitalSHSATModule__bucketItemBackground--selected'
                            : 'digitalSHSATModule__bucketItemBackground'
                        }
                    >
                        <div
                            className='digitalSHSATModule__bucketItem'
                            data-name={item.id}
                            onClick={(event) => { event.stopPropagation(); setSelectedItemId(item.id); }}
                        >
                            <p className='katex'>{item.answer}</p>
                        </div>
                        <p className='katex'>{item.answer}</p>
                    </div>
                ))}
            </div>
            <div className='digitalSHSATModule__buckets'>
                {question?.buckets.map(bucket => (
                    <div
                        key={bucket.bucketId}
                        data-name={bucket.bucketId}
                        className={(selectedItemId !== null && Array.isArray(currentAnswer) && 
                            !currentAnswer.find(answer => (answer[0] === bucket.bucketId && answer[1] === selectedItemId)) &&
                            currentAnswer.filter(answer => answer[0] === bucket.bucketId).length < (bucket.maxItems
                                ? Math.min(bucket.maxItems, question.dragAndDropItems.length - 1)
                                : question.dragAndDropItems.length - 1
                            )
                        )
                            ? 'digitalSHSATModule__bucket digitalSHSATModule__bucketDestination'
                            : 'digitalSHSATModule__bucket'
                        }
                        onClick={(selectedItemId !== null && Array.isArray(currentAnswer) && 
                            !currentAnswer.find(answer => (answer[0] === bucket.bucketId && answer[1] === selectedItemId)) &&
                            currentAnswer.filter(answer => answer[0] === bucket.bucketId).length < (bucket.maxItems
                                ? Math.min(bucket.maxItems, question.dragAndDropItems.length - 1)
                                : question.dragAndDropItems.length - 1
                            )
                        ) ? () => handleBucketAdd(Number(bucket.bucketId)) : null}
                    >
                        <h4 className='katex digitalSHSATModule__bucketName'>
                            {bucket.name}
                        </h4>
                        {question?.dragAndDropItems.filter(item => (
                            Array.isArray(currentAnswer) && currentAnswer.find(answer => (answer[0] === bucket.bucketId && answer[1] === item.id))
                        )).map(item => (
                            <div
                                key={`${bucket.bucketId}-${item.id}`}
                                className={selectedItemId === item.id
                                    ? 'digitalSHSATModule__bucketItemBackground digitalSHSATModule__bucketItemBackground--selected'
                                    : 'digitalSHSATModule__bucketItemBackground'
                                }
                            >
                                <div
                                    className='digitalSHSATModule__bucketItem'
                                    data-name={item.id}
                                    onClick={(event) => { event.stopPropagation(); setSelectedItemId(item.id); }}
                                >
                                    <p className='katex'>{item.answer}</p>
                                </div>
                                <p className='katex'>{item.answer}</p>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default DragAndDropBucket;
